import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { cumulativeAssessmentApi, type MathHeadLaunchResponse } from '@/api/cumulativeAssessment'
import { ExitConfirmDialog } from '@/components/learner/ExitConfirmDialog'
import { Button } from '@/components/learner/ui/Button'
import { LearnerLoading } from '@/components/learner/ui/LearnerLoading'
import { getApiErrorCode, getApiErrorMessage } from '@/utils/apiError'
import {
  formatRemainingTime,
  iframeOrigin,
  parseBiggoCompletionMessage,
  parseBiggoStartedMessage,
  remainingMs,
  resultConfirmDelayMs,
  type BiggoStartedMessage,
} from './biggoMessages'

type Phase = 'launching' | 'running' | 'confirming' | 'confirmExhausted' | 'error'

const SLOW_LOADING_MS = 8000
const TODAY_PATH = '/my/today'

/**
 * Math Head Test host page: embeds the BIGGO Assessment Run (ADR 0009).
 *
 * Launch → iframe (one-time ticket) → `started` message drives the remaining-time header →
 * `completed` message triggers backend confirm (1s/2s/4s retries) → back to Today Class.
 * The learner cannot cancel and never sees a score here; BIGGO world names/keys are never rendered.
 */
export default function MathHeadAssessment() {
  const { assessmentId = '' } = useParams<{ assessmentId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation('learner')
  const numericAssessmentId = Number(assessmentId)

  const [phase, setPhase] = useState<Phase>('launching')
  const [launch, setLaunch] = useState<MathHeadLaunchResponse | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [frameLoaded, setFrameLoaded] = useState(false)
  const [slowLoading, setSlowLoading] = useState(false)
  const [started, setStarted] = useState<{ message: BiggoStartedMessage; clientNow: number } | null>(null)
  const [remaining, setRemaining] = useState<number | null>(null)
  const [leaveOpen, setLeaveOpen] = useState(false)
  const [launchKey, setLaunchKey] = useState(0)

  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const confirmingRef = useRef(false)
  const unmountedRef = useRef(false)

  const goToday = useCallback(() => navigate(TODAY_PATH, { replace: true }), [navigate])

  // 1. launch (or resume) the run
  useEffect(() => {
    unmountedRef.current = false
    let active = true
    setPhase('launching')
    setLaunch(null)
    setErrorCode(null)
    setErrorMessage(null)
    setFrameLoaded(false)
    setSlowLoading(false)
    ;(async () => {
      try {
        const response = await cumulativeAssessmentApi.launchMathHead(numericAssessmentId, {
          embedOrigin: window.location.origin,
        })
        if (!active) return
        setLaunch(response.data.data)
        setPhase('running')
      } catch (error) {
        if (!active) return
        setErrorCode(getApiErrorCode(error) ?? null)
        setErrorMessage(getApiErrorMessage(error, t))
        setPhase('error')
      }
    })()
    return () => {
      active = false
      unmountedRef.current = true
    }
  }, [numericAssessmentId, launchKey, t])

  // 2. slow-loading notice for the iframe
  useEffect(() => {
    if (phase !== 'running' || frameLoaded) return
    const timer = window.setTimeout(() => setSlowLoading(true), SLOW_LOADING_MS)
    return () => window.clearTimeout(timer)
  }, [phase, frameLoaded])

  // 3. confirm with backend after the iframe reports completion (BIGGO status is the truth)
  const runConfirm = useCallback(async () => {
    if (!launch || confirmingRef.current) return
    confirmingRef.current = true
    setPhase('confirming')
    for (let attempt = 0; ; attempt += 1) {
      try {
        const response = await cumulativeAssessmentApi.confirmMathHead(launch.assessmentId, launch.runId)
        const result = response.data.data
        if (result.runStatus === 'COMPLETED' || result.runStatus === 'CANCELLED') {
          if (!unmountedRef.current) goToday()
          return
        }
      } catch {
        // retry below; BIGGO or network hiccups are expected right at the deadline
      }
      const delay = resultConfirmDelayMs(attempt)
      if (delay == null || unmountedRef.current) break
      await new Promise((resolve) => window.setTimeout(resolve, delay))
    }
    confirmingRef.current = false
    if (!unmountedRef.current) setPhase('confirmExhausted')
  }, [goToday, launch])

  // 4. postMessage listener (origin = iframe url origin, source = iframe window)
  useEffect(() => {
    if (!launch) return
    const expectedOrigin = iframeOrigin(launch.iframeUrl)
    if (!expectedOrigin) return
    function onMessage(event: MessageEvent) {
      const context = { expectedOrigin: expectedOrigin as string, iframeWindow: iframeRef.current?.contentWindow }
      const startedMessage = parseBiggoStartedMessage(event, context)
      if (startedMessage) {
        setStarted({ message: startedMessage, clientNow: Date.now() })
        return
      }
      if (parseBiggoCompletionMessage(event, context)) {
        void runConfirm()
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [launch, runConfirm])

  // 5. remaining-time tick
  useEffect(() => {
    if (!started) return
    const tick = () => setRemaining(remainingMs(started.message, started.clientNow, Date.now()))
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [started])

  // 6. leaving guard: browser back re-opens the dialog; tab close gets the native prompt
  useEffect(() => {
    if (phase !== 'running' && phase !== 'confirming') return
    window.history.pushState({ mathHeadGuard: true }, '', window.location.href)
    function onPopState() {
      window.history.pushState({ mathHeadGuard: true }, '', window.location.href)
      setLeaveOpen(true)
    }
    function onBeforeUnload(event: BeforeUnloadEvent) {
      // preventDefault() is the standard way to request the native "leave page?" prompt.
      event.preventDefault()
    }
    window.addEventListener('popstate', onPopState)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('popstate', onPopState)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [phase])

  if (phase === 'launching') return <LearnerLoading />

  if (phase === 'error') {
    return (
      <ErrorPanel
        code={errorCode}
        message={errorMessage}
        onRetry={() => setLaunchKey((key) => key + 1)}
        onToday={goToday}
        t={t}
      />
    )
  }

  if (phase === 'confirmExhausted') {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-lc-gray-50 p-6 text-center">
        <h1 className="text-lc-headline-3 text-lc-gray-900">{t('cumulative.mathHead.confirmFailedTitle')}</h1>
        <p className="max-w-md text-lc-body-2 text-lc-gray-700">{t('cumulative.mathHead.confirmFailed')}</p>
        <Button variant="primary" size="M" onClick={goToday}>{t('cumulative.mathHead.backToToday')}</Button>
      </main>
    )
  }

  return (
    <main className="flex h-screen flex-col bg-lc-gray-900">
      <header className="flex items-center justify-between gap-4 px-4 py-3 text-white">
        <h1 className="text-lc-body-1 font-extrabold">{t('cumulative.mathHead.title')}</h1>
        <div className="flex items-center gap-4">
          {remaining != null ? (
            <span
              role="timer"
              aria-label={t('cumulative.mathHead.remaining')}
              className={`rounded-full px-3 py-1 text-lc-caption-1 font-bold tabular-nums ${
                remaining < 60_000 ? 'bg-lc-orange-dark text-white' : 'bg-white/15 text-white'
              }`}
            >
              {formatRemainingTime(remaining)}
            </span>
          ) : null}
          <button
            type="button"
            className="text-lc-caption-1 font-semibold text-white/80 underline-offset-2 hover:underline"
            onClick={() => setLeaveOpen(true)}
          >
            {t('cumulative.mathHead.leave')}
          </button>
        </div>
      </header>

      <div className="relative flex-1">
        {launch ? (
          <iframe
            ref={iframeRef}
            title={t('cumulative.mathHead.title')}
            src={launch.iframeUrl}
            allow="autoplay; fullscreen"
            hidden={phase === 'confirming'}
            className="h-full w-full border-0 bg-black"
            onLoad={() => setFrameLoaded(true)}
          />
        ) : null}
        {(!frameLoaded || phase === 'confirming') ? (
          <div
            role="status"
            aria-live="polite"
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-lc-gray-900 text-white"
          >
            <p className="text-lc-body-1 font-bold">
              {phase === 'confirming'
                ? t('cumulative.mathHead.confirming')
                : slowLoading
                  ? t('cumulative.mathHead.slow')
                  : t('cumulative.mathHead.loading')}
            </p>
          </div>
        ) : null}
      </div>

      <ExitConfirmDialog
        open={leaveOpen}
        title={t('cumulative.mathHead.leaveTitle')}
        message={t('cumulative.mathHead.leaveBody')}
        onCancel={() => setLeaveOpen(false)}
        onConfirm={() => {
          setLeaveOpen(false)
          goToday()
        }}
      />
    </main>
  )
}

function ErrorPanel({
  code,
  message,
  onRetry,
  onToday,
  t,
}: {
  code: string | null
  message: string | null
  onRetry: () => void
  onToday: () => void
  t: ReturnType<typeof useTranslation>['t']
}) {
  const retryable = code === 'BIGGO_UNAVAILABLE' || code == null
  const body = code === 'BIGGO_UNAVAILABLE'
    ? t('cumulative.mathHead.error.unavailable')
    : code === 'CUMULATIVE_MATH_HEAD_NOT_READY'
      ? t('cumulative.mathHead.notReady')
      : code === 'SESSION_NOT_OPEN'
        ? t('cumulative.mathHead.error.sessionClosed')
        : code === 'BIGGO_ATTEMPT_CONFLICT'
          ? t('cumulative.mathHead.error.conflict')
          : message ?? t('cumulative.mathHead.error.generic')
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-lc-gray-50 p-6 text-center">
      <h1 className="text-lc-headline-3 text-lc-gray-900">{t('cumulative.mathHead.error.title')}</h1>
      <p className="max-w-md text-lc-body-2 text-lc-gray-700">{body}</p>
      <div className="flex flex-wrap justify-center gap-3">
        {retryable ? (
          <Button variant="primary" size="M" onClick={onRetry}>{t('cumulative.mathHead.retry')}</Button>
        ) : null}
        <Button variant="outline" size="M" onClick={onToday}>{t('cumulative.mathHead.backToToday')}</Button>
      </div>
    </main>
  )
}
